<!-- Copyright (c) 2022-2024, NVIDIA CORPORATION & AFFILIATES. All rights reserved. -->

<template>
    <div class="artifact-table-container">
        <div class="heading-container">
            <h3>Migration Results - {{filename}}</h3>
            <button v-if="simUrl" @click="gotoSim">View in Air</button>
        </div>
        <div class='error-message'>{{error}}</div>
        <table id="artifact-table" v-if="artifacts.length">
            <thead><tr>
                <th>File name</th>
                <th class="actions-column">Actions</th>
            </tr></thead>
            <tbody>
                <tr v-for="artifact in artifacts" :key="artifact.name">
                    <td class="file-name">
                        <span>{{getArtifactName(artifact)}}</span>
                        <span class="material-icons help-icon"
                            v-tooltip="{
                                trigger: 'hover',
                                content: artifact.desc,
                                classes: 'tooltiptext'
                            }"
                        >
                            help_outline
                        </span>
                    </td>
                    <td class="actions-column">
                        <span class="material-icons nvidia-green px-10 cursor-pointer"
                            alt="Preview"
                            title="Preview"
                            @click="previewArtifact(artifact)"
                            @keyup.enter="previewArtifact(artifact)"
                            tabindex="0"
                        >
                            preview
                        </span>
                        <span class="material-icons nvidia-green px-10 cursor-pointer"
                            alt="Download"
                            title="Download"
                            @click="downloadArtifact(artifact)"
                            @keyup.enter="downloadArtifact(artifact)"
                            tabindex="0"
                        >
                            download
                        </span>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>
<script>
import { saveAs } from 'file-saver';

import ArtifactPreview from './ArtifactPreview.vue';

export default {
    name: 'ArtifactTable',

    props: {
        filename: String,
        artifacts: Array,
        error: String,
        simUrl: String,
    },

    data() {
        return {
            artifactContent: {},
        };
    },

    methods: {
        async downloadArtifact(artifact) {
            saveAs(new Blob([await this.getArtifactContent(artifact)]), `${artifact.name}.txt`);
        },
        async getArtifactContent(artifact) {
            // TODO: loading spinner
            if (!this.artifactContent[artifact.name]) {
                let url = `${window.airschipp.api}/v1/result?artifact=`;
                url += artifact.path;
                let data = {};
                try {
                    const res = await fetch(url);
                    data = await res.json();
                } catch (err) {
                    console.error(err);
                }
                this.artifactContent[artifact.name] = data.content || 'Unable to fetch data';
            }
            return this.artifactContent[artifact.name];
        },
        getArtifactName(artifact) {
            const name = artifact.name.replaceAll('-', ' ');
            return name.replaceAll('nvue', 'NVUE');
        },
        gotoSim() {
            window.location.assign(this.simUrl);
        },
        async previewArtifact(artifact) {
            this.$modal.show(
                ArtifactPreview,
                {
                    content: await this.getArtifactContent(artifact),
                    name: this.getArtifactName(artifact),
                },
                { height: '70%', width: '70%' },
            );
        },
    },
};
</script>
<style lang="scss" scoped>
@import '~@cumulus/air-ui-kit/variables.scss';

.error-message {
    font-weight: bold;
    text-align: center;
    background-color: $radical-red;
    color: white;
}

.filename {
    text-align: right;
    padding: 0;
}

.artifact-table-container {
    text-align: left;

    .actions-column {
        text-align: center;
        width: 20%;

        .material-icons {
            font-size: 32px;
        }
    }

    .file-name {
        text-transform: capitalize;
        display: flex;
        align-items: center;
    }
}

.heading-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.help-icon {
    cursor: default;
    font-size: 18px;
    padding-left: 10px;
}

::v-deep .v-popover {
    display: inline;
}
</style>
